import {
  AnimationBuilder,
  createAnimation,
  iosTransitionAnimation,
} from '@ionic/angular/standalone';
import { isTabPagerLiteMode } from 'src/app/utils/tab-pager-performance';

/** Same breakpoint used for desktop layout (tab bar, home grid, …). */
export const DESKTOP_MIN_WIDTH_PX = 992;

export const DESKTOP_FADE_DURATION_MS = 200;

export function isDesktopViewport(): boolean {
  return (
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH_PX
  );
}

type NavTransitionOpts = {
  enteringEl: HTMLElement;
  leavingEl?: HTMLElement;
  direction?: 'forward' | 'back';
};

function getPageEl(element: HTMLElement): HTMLElement {
  if (element.classList.contains('ion-page')) {
    return element;
  }
  const page = element.querySelector(
    ':scope > .ion-page, :scope > ion-nav, :scope > ion-tabs',
  );
  return (page as HTMLElement | null) ?? element;
}

export function clearPageTransitionInlineStyles(
  el: HTMLElement | null | undefined,
): void {
  if (!el) {
    return;
  }

  el.style.removeProperty('opacity');
  el.style.removeProperty('transform');
  el.style.removeProperty('z-index');
  el.style.removeProperty('background');
  el.classList.remove('ion-page-invisible');
}

/**
 * Clears inline styles left by interrupted transitions.
 * Only targets elements that look stuck (invisible overlay), not mid-animation pages.
 */
export function clearStuckPageTransitionStyles(
  root: ParentNode = document,
): void {
  root.querySelectorAll<HTMLElement>('.ion-page').forEach((page) => {
    const hasSlidingClass = page.classList.contains(TAB_SLIDING_CLASS);
    const opacity = page.style.opacity;
    const zIndex = page.style.zIndex;
    const isHidden =
      page.classList.contains('ion-page-hidden') ||
      page.getAttribute('aria-hidden') === 'true';

    const looksStuck =
      hasSlidingClass ||
      opacity === '0' ||
      opacity === '0.01' ||
      (zIndex === '99' && opacity !== '' && opacity !== '1');

    // Hidden stack pages, or visible pages still carrying transition overlay styles.
    if (looksStuck && (isHidden || zIndex === '99' || hasSlidingClass)) {
      page.classList.remove(TAB_SLIDING_CLASS);
      clearPageTransitionInlineStyles(page);
    }
  });
}

function fadeNavAnimation(_baseEl: HTMLElement, opts: NavTransitionOpts) {
  const backDirection = opts.direction === 'back';
  const enteringPage = getPageEl(opts.enteringEl);
  const leavingPage = opts.leavingEl ? getPageEl(opts.leavingEl) : undefined;

  const rootAnimation = createAnimation('desktop-fade-transition')
    .duration(DESKTOP_FADE_DURATION_MS)
    .easing(
      backDirection
        ? 'cubic-bezier(0.47,0,0.745,0.715)'
        : 'cubic-bezier(0.36,0.66,0.04,1)',
    )
    .fill('both');

  const enteringAnimation = createAnimation('desktop-fade-in')
    .addElement(enteringPage)
    .fill('both')
    .beforeRemoveClass('ion-page-invisible')
    .fromTo('opacity', 0.01, 1);

  rootAnimation.addAnimation(enteringAnimation);

  if (leavingPage && backDirection) {
    const leavingAnimation = createAnimation('desktop-fade-out')
      .addElement(leavingPage)
      .fill('both')
      .fromTo('opacity', 1, 0.01);
    rootAnimation.addAnimation(leavingAnimation);
  }

  rootAnimation.onFinish(() => {
    clearPageTransitionInlineStyles(leavingPage);
    enteringPage.style.removeProperty('z-index');
    enteringPage.classList.remove('ion-page-invisible');
  });

  return rootAnimation;
}

export const responsiveNavAnimation: AnimationBuilder = (baseEl, opts) => {
  if (isDesktopViewport()) {
    return fadeNavAnimation(baseEl, opts);
  }
  return iosTransitionAnimation(baseEl, opts);
};

/** Pager-style tab change; matches `--re-motion-slow`. */
export const TAB_SWITCH_DURATION_MS = 320;
/** Slightly softer than Ionic swipe-back; keeps release within Material ~400ms feel. */
export const TAB_SWIPE_RELEASE_DURATION_SCALE = 1.1;
export const TAB_SWIPE_RELEASE_DURATION_MAX_MS = 500;
export const TAB_SLIDING_CLASS = 're-tab-sliding';
const TAB_PAGER_BG = 'var(--ion-background-color, var(--re-color-bg))';

function prepareTabPagerPages(
  leavingPage: HTMLElement,
  enteringPage: HTMLElement,
  forward: boolean,
): void {
  for (const page of [leavingPage, enteringPage]) {
    page.classList.remove('ion-page-hidden', 'ion-page-invisible');
  }
  leavingPage.style.setProperty('z-index', '10');
  enteringPage.style.setProperty('z-index', '11');
  leavingPage.style.setProperty('background', TAB_PAGER_BG);
  enteringPage.style.setProperty('background', TAB_PAGER_BG);

  const enterFrom = forward ? '100%' : '-100%';
  enteringPage.style.setProperty('transform', `translate3d(${enterFrom},0,0)`);
  leavingPage.style.setProperty('transform', 'translate3d(0,0,0)');
  // Force layout + compositor snapshot before the slide starts.
  void enteringPage.offsetWidth;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Full-width horizontal pager between main tabs. Ionic sets tab-switch
 * duration to 0 (iOS UITabBar is instant), so this animation is played by
 * `reSlidingTabs` while `.re-tab-sliding` keeps both pages visible.
 */
export function createTabPagerTransition(
  from: HTMLElement,
  to: HTMLElement,
  forward: boolean,
) {
  const leavingPage = getPageEl(from);
  const enteringPage = getPageEl(to);

  if (prefersReducedMotion() || isDesktopViewport() || isTabPagerLiteMode()) {
    return null;
  }

  leavingPage.classList.add(TAB_SLIDING_CLASS);
  enteringPage.classList.add(TAB_SLIDING_CLASS);
  prepareTabPagerPages(leavingPage, enteringPage, forward);

  const enterFrom = forward ? '100%' : '-100%';
  const leaveTo = forward ? '-100%' : '100%';

  const rootAnimation = createAnimation('tab-pager-transition')
    .duration(TAB_SWITCH_DURATION_MS)
    .easing('cubic-bezier(0.32, 0.72, 0, 1)')
    .fill('both');

  const enteringAnimation = createAnimation('tab-pager-in')
    .addElement(enteringPage)
    .fill('both')
    .beforeRemoveClass('ion-page-invisible')
    .fromTo(
      'transform',
      `translate3d(${enterFrom},0,0)`,
      'translate3d(0,0,0)',
    );

  const leavingAnimation = createAnimation('tab-pager-out')
    .addElement(leavingPage)
    .fill('both')
    .fromTo('transform', 'translate3d(0,0,0)', `translate3d(${leaveTo},0,0)`);

  rootAnimation.addAnimation([enteringAnimation, leavingAnimation]);

  const pages = [leavingPage, enteringPage];
  const cleanup = () => {
    for (const page of pages) {
      page.classList.remove(TAB_SLIDING_CLASS);
      clearPageTransitionInlineStyles(page);
    }
  };

  return { animation: rootAnimation, cleanup, pages };
}

export const NAV_TRANSITION_CLEANUP_DELAY_MS =
  Math.max(DESKTOP_FADE_DURATION_MS, TAB_SWITCH_DURATION_MS) + 120;

export function clamp(min: number, value: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Same commit rule as Ionic's iOS swipe-to-go-back: finish if the finger
 * is past halfway or the velocity is clearly in that direction; remaining
 * distance / velocity becomes the snap duration (capped at 500ms).
 */
export function tabPagerSwipeDecision(
  deltaTowardComplete: number,
  velocityTowardComplete: number,
  width: number,
): { shouldComplete: boolean; step: number; duration: number } {
  const step = clamp(0, deltaTowardComplete / width, 0.9999);
  const shouldComplete =
    velocityTowardComplete > 0.2 || deltaTowardComplete > width / 2;
  const missing = shouldComplete ? 1 - step : step;
  const missingDistance = missing * width;
  let duration = 0;
  if (missingDistance > 5) {
    duration = Math.min(
      (missingDistance /
        Math.max(Math.abs(velocityTowardComplete), 0.12)) *
        TAB_SWIPE_RELEASE_DURATION_SCALE,
      TAB_SWIPE_RELEASE_DURATION_MAX_MS,
    );
  } else if (missing > 0.02) {
    // Gesture end can report deltaX≈0; keep a short slide instead of an instant snap.
    duration = Math.min(
      missing * TAB_SWITCH_DURATION_MS * TAB_SWIPE_RELEASE_DURATION_SCALE,
      TAB_SWITCH_DURATION_MS * TAB_SWIPE_RELEASE_DURATION_SCALE,
    );
  }
  return {
    shouldComplete,
    step: step <= 0 ? 0.01 : step,
    duration,
  };
}
